import React, { useEffect, useState } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import {
  AE_BTN_PRIMARY,
  AE_BTN_SECONDARY,
  AE_CARD,
  AE_MUTED,
  AE_SECTION_TITLE,
} from '@/components/assessment-engine/layout/aeTheme';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import type { AeResponseDetail } from '@/types/assessmentEngine';

interface CampaignResponseDetailModalProps {
  campaignId: string;
  responseId: string;
  isAnonymous: boolean;
  onClose: () => void;
  onUpdated: () => void;
}

/** Admin modal — review one respondent's scores and answers. */
export const CampaignResponseDetailModal: React.FC<CampaignResponseDetailModalProps> = ({
  campaignId,
  responseId,
  isAnonymous,
  onClose,
  onUpdated,
}) => {
  const { t } = useLanguage();
  const [detail, setDetail] = useState<AeResponseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    assessmentEngineService
      .getCampaignResponseDetail(campaignId, responseId)
      .then(setDetail)
      .catch(() => toast.error(t('assessmentEngine.hub.responseDetailFailed')))
      .finally(() => setLoading(false));
  }, [campaignId, responseId, t]);

  const isSubmitted =
    detail?.response.status === 'complete' || detail?.response.status === 'incomplete';

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className={`${AE_CARD} max-w-3xl w-full max-h-[90vh] overflow-y-auto space-y-4`}>
        <div className="flex items-start justify-between gap-3">
          <h3 className={AE_SECTION_TITLE}>{t('assessmentEngine.hub.responseDetailTitle')}</h3>
          <button type="button" className={AE_BTN_SECONDARY} onClick={onClose}>
            {t('assessmentEngine.hub.close')}
          </button>
        </div>

        {loading && <p className={AE_MUTED}>{t('assessmentEngine.hub.loadingResponseDetail')}</p>}

        {!loading && detail && (
          <>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              {!isAnonymous && (
                <div>
                  <dt className="text-[var(--color-secondary-text)]">{t('assessmentEngine.hub.email')}</dt>
                  <dd className="text-[var(--color-surface-text)]">
                    {detail.response.respondent_email ?? '—'}
                  </dd>
                </div>
              )}
              {!isAnonymous && detail.response.email_domain && (
                <div>
                  <dt className="text-[var(--color-secondary-text)]">
                    {t('assessmentEngine.hub.emailDomain')}
                  </dt>
                  <dd className="text-[var(--color-surface-text)]">{detail.response.email_domain}</dd>
                </div>
              )}
              <div>
                <dt className="text-[var(--color-secondary-text)]">
                  {t('assessmentEngine.hub.responseStatus')}
                </dt>
                <dd className="text-[var(--color-surface-text)]">{detail.response.status}</dd>
              </div>
              <div>
                <dt className="text-[var(--color-secondary-text)]">
                  {t('assessmentEngine.hub.timePeriod')}
                </dt>
                <dd className="text-[var(--color-surface-text)]">
                  {detail.response.time_period_key || '—'}
                </dd>
              </div>
            </dl>

            {detail.sections.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[var(--color-surface-text)] mb-2">
                  {t('assessmentEngine.hub.sectionScores')}
                </h4>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)] text-left">
                      <th className="py-1">{t('assessmentEngine.report.section')}</th>
                      <th className="py-1">{t('assessmentEngine.results.yourScore')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.sections.map((sec) => (
                      <tr key={sec.section_id} className="border-b border-[var(--color-border)]">
                        <td className="py-1 text-[var(--color-surface-text)]">{sec.section_name}</td>
                        <td className="py-1 text-[var(--color-surface-text)]">
                          {sec.mean_score != null ? sec.mean_score.toFixed(2) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {detail.answers.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[var(--color-surface-text)] mb-2">
                  {t('assessmentEngine.hub.answers')}
                </h4>
                <ul className="space-y-3 text-sm">
                  {detail.answers.map((a) => (
                    <li key={a.question_id} className="border-b border-[var(--color-border)] pb-2">
                      <p className="font-medium text-[var(--color-surface-text)]">{a.question_text}</p>
                      <p className="text-xs text-[var(--color-secondary-text)]">{a.section_name}</p>
                      <p className="text-[var(--color-surface-text)] mt-1">
                        {a.is_na
                          ? 'N/A'
                          : a.answer_numeric != null
                            ? String(a.answer_numeric)
                            : a.answer_text ?? '—'}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {isSubmitted && (
              <div className="flex justify-end">
                <button
                  type="button"
                  disabled={actionBusy}
                  className={AE_BTN_PRIMARY}
                  onClick={async () => {
                    setActionBusy(true);
                    try {
                      if (detail.response.excluded_from_aggregate) {
                        await assessmentEngineService.restoreResponse(campaignId, responseId);
                        toast.success(t('assessmentEngine.hub.responseRestored'));
                      } else {
                        await assessmentEngineService.invalidateResponse(campaignId, responseId);
                        toast.success(t('assessmentEngine.hub.responseInvalidated'));
                      }
                      onUpdated();
                      onClose();
                    } catch {
                      toast.error(t('assessmentEngine.hub.responseActionFailed'));
                    } finally {
                      setActionBusy(false);
                    }
                  }}
                >
                  {detail.response.excluded_from_aggregate
                    ? t('assessmentEngine.hub.restoreResponse')
                    : t('assessmentEngine.hub.invalidateResponse')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
